import { useCallback, useEffect, useRef, useState } from "react";
import { MBRADIO, MBRADIO_ROOM } from "@picnic-field/room-protocol";
import { readConfig } from "../../../lib/config";
import type { RadioApi, RadioTicket } from "../api/radioApi";
import { MB_RADIO_IN, acceptBenchRadio, initialPlaza, packetOf, plazaIn, plazaOut } from "../lib/radio";
import type { Plaza } from "../lib/radio";
import type { LiveWindow } from "./useMicrobitLive";

/** 광장 룸 한 개(전송 — 시험이 바꿔 끼운다) */
export interface RadioRoom {
  /** 광장에서 내 연결 id(명단에서 나를 뺄 때) */
  readonly id: string;
  send(g: number, p: number[], air?: boolean): void;
  onRecv(cb: (m: { name: string; g: number; p: number[]; air?: boolean }) => void): void;
  onMembers(cb: (m: { id: string; name: string; role: string }[]) => void): void;
  onLeave(cb: (code: number) => void): void;
  leave(): Promise<void>;
}
export type RadioConnect = (t: RadioTicket) => Promise<RadioRoom>;

/** Colyseus — 코스마다 하나인 광장에 joinOrCreate. SDK 는 광장을 쓸 때만 늦게 불러온다 */
export const colyseusRadio: RadioConnect = async (t) => {
  const { Client } = await import("@colyseus/sdk");
  const room = await new Client(t.wsUrl).joinOrCreate(MBRADIO_ROOM, { token: t.token, course: t.course });
  room.reconnection.enabled = false;   // 다시 붙기는 아래 훅이 새 토큰으로
  room.onMessage("*", () => {});
  return {
    id: room.sessionId,
    send: (g, p, air) => room.send(MBRADIO.send, air ? { g, p, air: true } : { g, p }),
    onRecv: (cb) => void room.onMessage(MBRADIO.recv, (m: { name?: unknown; g?: unknown; p?: unknown; air?: unknown }) => {
      const pk = packetOf(m?.g, m?.p);
      if (pk) cb({ name: typeof m.name === "string" ? m.name.slice(0, 24) : "", ...pk, ...(m.air === true ? { air: true } : {}) });
    }),
    onMembers: (cb) => void room.onMessage(MBRADIO.members, (m: unknown) => cb(Array.isArray(m) ? (m as { id: string; name: string; role: string }[]).slice(0, 40) : [])),
    onLeave: (cb) => void room.onLeave((code: number) => cb(code)),
    leave: async () => { await room.leave(true).catch(() => {}); },
  };
};

export type RadioStatus = "idle" | "connecting" | "on" | "off";
const RETRY_MS = 5000;
const MAX_TRIES = 6;

/**
 * 라디오 광장(PICNIC-MB-FIELD-001 §6 2단계) — 광장을 고르거나(active) 내 시뮬레이터가 라디오를 처음 보내면 그때 붙는다(쓰지 않는 아이는 붙지 않는다).
 *  - 내 시뮬레이터 → 광장: Bench 탭이 보낸 `bench.mbRadio`(설정된 Bench 출처만) → 룸 send.
 *  - 광장 → 내 시뮬레이터: 룸 recv → Bench 탭에 `field.mbRadio`(Bench 출처로만). Bench 탭 창은 **그 탭이 보낸 소식의 source** 로 안다 —
 *    이 탭을 새로 고쳐도(연 창 핸들을 잃어도) Bench 탭의 화면 소식이 1초마다 오므로 곧 다시 잇는다.
 *  - 끊기면 5초 뒤 새 토큰으로 다시(6번까지).
 *  - 진짜 보드 게이트웨이(§6.7 — lib/gateway): `tap` 이 광장에서 받은 것 · 내 시뮬레이터가 보낸 것을 모두 본다(게이트웨이가 공중으로) ·
 *    `sendAir` 가 공중에서 들은 패킷을 광장으로(air 표시).
 */
export function useMbRadio(api: RadioApi | null, active: boolean,
  opts: { connect?: RadioConnect; win?: LiveWindow; benchOrigin?: string | null; accept?: (ev: MessageEvent) => boolean;
    tap?: (m: { g: number; p: number[]; air: boolean; from: "plaza" | "me" }) => void } = {}) {
  const connect = opts.connect ?? colyseusRadio;
  const win: LiveWindow = opts.win ?? window;
  const benchOrigin = opts.benchOrigin === undefined ? readConfig().benchOrigin : opts.benchOrigin;
  const [status, setStatus] = useState<RadioStatus>("idle");
  const [members, setMembers] = useState<{ id: string; name: string; role: string }[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [plaza, setPlaza] = useState<Plaza>(initialPlaza);
  const [want, setWant] = useState(active);
  const roomRef = useRef<RadioRoom | null>(null);
  const benchWin = useRef<MessageEventSource | null>(null);
  const tries = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const acceptRef = useRef(opts.accept);
  acceptRef.current = opts.accept;
  const tapRef = useRef(opts.tap);
  tapRef.current = opts.tap;

  useEffect(() => { if (active) setWant(true); }, [active]);

  // Bench 탭 소식 — 창을 기억하고, 내 라디오는 광장으로
  useEffect(() => {
    if (!benchOrigin) return;
    const h = (ev: MessageEvent) => {
      if (ev.origin !== benchOrigin || (acceptRef.current && !acceptRef.current(ev))) return;
      const t = (ev.data as { type?: unknown } | null)?.type;
      if ((t === "bench.mbLive" || t === "bench.mbRadio") && ev.source) benchWin.current = ev.source;
      const pk = acceptBenchRadio(ev, benchOrigin);
      if (!pk) return;
      setWant(true);
      setPlaza((s) => plazaOut(s, pk.g, pk.p));
      roomRef.current?.send(pk.g, pk.p);
      tapRef.current?.({ g: pk.g, p: pk.p, air: false, from: "me" });
    };
    win.addEventListener("message", h);
    return () => win.removeEventListener("message", h);
  }, [win, benchOrigin]);

  useEffect(() => {
    if (!want || !api) return;
    let alive = true;
    let room: RadioRoom | null = null;
    setStatus("connecting");
    void (async () => {
      try {
        const t = await api.ticket();
        room = await connect(t);
        if (!alive) { void room.leave(); return; }
        roomRef.current = room;
        setSelfId(room.id);
        tries.current = 0;
        setStatus("on");
        room.onMembers(setMembers);
        room.onRecv((m) => {
          setPlaza((s) => plazaIn(s, m.name, m.g, m.p, !!m.air));
          tapRef.current?.({ g: m.g, p: m.p, air: !!m.air, from: "plaza" });
          try { (benchWin.current as Window | null)?.postMessage({ type: MB_RADIO_IN, v: 1, g: m.g, p: m.p }, benchOrigin ?? ""); } catch { /* Bench 탭이 닫혔다 */ }
        });
        room.onLeave(() => {
          if (!alive) return;
          roomRef.current = null;
          setStatus("off");
          setMembers([]);
          if (++tries.current <= MAX_TRIES) setTimeout(() => alive && setAttempt((n) => n + 1), RETRY_MS);
        });
      } catch {
        if (!alive) return;
        setStatus("off");
        if (++tries.current <= MAX_TRIES) setTimeout(() => alive && setAttempt((n) => n + 1), RETRY_MS);
      }
    })();
    return () => {
      alive = false;
      roomRef.current = null;
      void room?.leave();
    };
  }, [want, api, connect, attempt, benchOrigin]);

  /** 게이트웨이 보드가 교실 공중에서 들은 패킷 → 광장(air) · 기록에도(📡) */
  const sendAir = useCallback((g: number, p: number[]) => {
    setWant(true);
    setPlaza((s) => plazaIn(s, "", g, p, true));
    roomRef.current?.send(g, p, true);
  }, []);

  return { status, members: members.filter((m) => m.id !== selfId), plaza, sendAir };
}
